/**
 * Duvar bots (pure): `clearOrder` proves a wall can be emptied; `revealPlan` is the "smart peek"
 * player — it opens the middle of the picture first and never bumps — used for grading, the
 * smoke test's autoplay and the video factory.
 */
import { blockerOf, cellIndex, columnOpen, createGame, freeBlocks, revealedFraction, tapBlock, type DuvarLevel, type DuvarState } from "./game";
import type { Picture } from "@enjoyment/core";

const BOT_PICTURE: Picture = { id: "bot", theme: "bot", size: 1, palette: [], pixels: "", label: { tr: "" } };

/** Exits in a valid order until the wall is empty (null if it ever got stuck — never for generated walls). */
export function clearOrder(level: DuvarLevel): number[] | null {
  const s = createGame(level, BOT_PICTURE);
  const order: number[] = [];
  while (s.left > 0) {
    const free = freeBlocks(s);
    if (!free.length) return null;
    for (const id of free) {
      tapBlock(s, id);
      order.push(id);
    }
  }
  return order;
}

/** Blocks still standing in a picture column. */
function columnLeft(s: DuvarState, x: number, y: number): number {
  let n = 0;
  for (let z = 0; z < s.level.d; z++) if (s.occ[cellIndex(s.level, x, y, z)]! >= 0) n++;
  return n;
}

/**
 * Taps (no bumps) the bot spends to open `target` of the picture: always the free block whose
 * column is nearest the centre and closest to opening. Deterministic.
 */
export function revealPlan(level: DuvarLevel, target: number): number[] {
  const s = createGame(level, BOT_PICTURE);
  const cx = (level.w - 1) / 2;
  const cy = (level.h - 1) / 2;
  const plan: number[] = [];
  while (s.left > 0 && revealedFraction(s) < target) {
    let best = -1;
    let bestScore = Infinity;
    for (const id of freeBlocks(s)) {
      const b = level.blocks[id]!;
      const score = Math.hypot(b.x - cx, b.y - cy) + columnLeft(s, b.x, b.y) * 0.75 + (columnOpen(s, b.x, b.y) ? 9 : 0);
      if (score < bestScore - 1e-9) {
        bestScore = score;
        best = id;
      }
    }
    if (best < 0) break;
    tapBlock(s, best);
    plan.push(best);
  }
  return plan;
}

/** Blocks free at the start (more = easier to begin) and the longest blocking chain (depth of planning). */
export function wallStats(level: DuvarLevel): { blocks: number; freeAtStart: number; longestChain: number } {
  const s = createGame(level, BOT_PICTURE);
  const memo = new Map<number, number>();
  const chain = (id: number, seen: Set<number>): number => {
    const known = memo.get(id);
    if (known !== undefined) return known;
    const blocker = blockerOf(s, id);
    if (blocker < 0 || seen.has(blocker)) return 0;
    seen.add(blocker);
    const v = 1 + chain(blocker, seen);
    memo.set(id, v);
    return v;
  };
  let longest = 0;
  for (const b of level.blocks) longest = Math.max(longest, chain(b.id, new Set([b.id])));
  return { blocks: level.blocks.length, freeAtStart: freeBlocks(s).length, longestChain: longest };
}
